import type { GetServerSideProps } from 'next'
import type { FC, ReactNode } from 'react'
import type { ReaderStory, StoryTitle } from '@/prakrit/components/stories/story-logic'
import type { NextStory } from '@/prakrit/components/stories/story-reader'
import type { ReviewCreditData } from '@/prakrit/lib/server/credits'

import { Layout } from '@/components/generals/layout'
import { Seo } from '@/components/generals/seo'
import { JsonLd, breadcrumbList } from '@/components/generals/json-ld'
import { StoryAwaiting, StoryReader } from '@/prakrit/components/stories/story-reader'
import ReviewCredit from '@/prakrit/components/common/review-credit'
import ReportIssueButton from '@/components/app/report-issue-button'
import { STORIES, getStory, storyReviewKey } from '@/prakrit/lib/content/stories'
import { canPreviewLessons } from '@/prakrit/lib/server/lessons'
import { reviewCredit } from '@/prakrit/lib/server/credits'
import { isVisibleStatus } from '@/prakrit/lib/server/flags'
import { readerStory, storyStatusesChecked } from '@/prakrit/components/stories/story-server'
import { ContentUnavailableNotice, PlanNotice } from '@/components/plans/plan-notice'
import { viewerOf } from '@/lib/server/access'
import { storyIsFree } from '@/lib/plans'

interface StoryPageProps {
    id: string
    kind: 'story' | 'dialogue'
    level: number
    title: StoryTitle
    /** null while the story awaits a teacher's review (and the visitor is not an editor or reviewer). */
    story: ReaderStory | null
    next: NextStory | null
    /** An editor or reviewer is reading a story learners cannot see yet. */
    preview: boolean
    /** After the free units, and the visitor has no plan or trial: nothing of the story left the server. */
    planLocked: boolean
    /** With planLocked: the account could not be checked just now, so the page says so instead of offering Premium. */
    accessUnavailable: boolean
    /** The review states could not be read just now: "try again", never "awaiting review". */
    contentUnavailable: boolean
    /** Review key of the story (story|id), sent with a language report. */
    reviewKey: string
    /** The teacher who verified or published the story, for the quality line (null: nothing is shown). */
    credit: ReviewCreditData | null
}

const StoryPage: FC<StoryPageProps> = ({
    id,
    kind,
    level,
    title,
    story,
    next,
    preview,
    planLocked,
    accessUnavailable,
    contentUnavailable,
    reviewKey,
    credit
}): ReactNode => (
    <Layout>
        <Seo
            title={`${title.hi} · ${title.en}`}
            description={`${title.pra} (${title.en}): a graded Prakrit ${kind === 'dialogue' ? 'dialogue' : 'story'} for after Unit ${level}, with tappable words, Hindi and English translations and questions.`}
        />
        <JsonLd
            id='breadcrumbs'
            data={breadcrumbList([
                { name: 'Suvaani', path: '/' },
                { name: 'Prakrit', path: '/prakrit' },
                { name: 'Stories', path: '/stories' },
                { name: title.en, path: `/stories/${id}` }
            ])}
        />
        {planLocked
            ? <PlanNotice kind='story' title={{ hi: title.hi, en: title.en }} unavailable={accessUnavailable} />
            : contentUnavailable
                ? <ContentUnavailableNotice kind='story' title={{ hi: title.hi, en: title.en }} />
                : story
                    ? (
                        <StoryReader
                            key={story.id}
                            story={story}
                            next={next}
                            preview={preview}
                            footer={(
                                <div className='flex flex-wrap items-center justify-between gap-x-4 gap-y-1'>
                                    <ReviewCredit credit={credit} />
                                    <ReportIssueButton
                                        course='prakrit'
                                        itemKey={reviewKey}
                                        itemLabel={`${title.pra} · ${title.hi} · ${title.en}`}
                                        className='-ml-3 sm:ml-auto'
                                    />
                                </div>
                            )}
                        />
                    )
                    : <StoryAwaiting title={title} kind={kind} level={level} />}
    </Layout>
)

export const getServerSideProps: GetServerSideProps<StoryPageProps> = async ({ params, req }) => {
    const id = typeof params?.id === 'string' ? params.id : ''
    const found = getStory(id)

    if (!found) return { notFound: true }

    const [{ statusOf, failed }, { user, access }] = await Promise.all([storyStatusesChecked(), viewerOf(req)])
    const reviewer = canPreviewLessons(user)
    const status = statusOf(found.id)
    // Early access: a plan or the trial also opens stories a teacher has verified but not yet published.
    const visibleTo = (s: string) => isVisibleStatus(s) || (access.full && s === 'verified')
    const visible = visibleTo(status)
    const base = {
        id: found.id,
        kind: found.kind,
        level: found.level,
        title: { ...found.title },
        planLocked: false,
        accessUnavailable: Boolean(access.unavailable),
        contentUnavailable: false,
        reviewKey: storyReviewKey(found.id),
        credit: null
    }

    if (!reviewer && !access.full && !storyIsFree(found.level)) return { props: { ...base, story: null, next: null, preview: false, planLocked: true } }

    // The review states did not load: nothing of the story leaves the server, and the page says "try again".
    if (failed && !reviewer) return { props: { ...base, story: null, next: null, preview: false, contentUnavailable: true } }

    // Awaiting review: no lines, questions or words leave the server.
    if (!visible && !reviewer) return { props: { ...base, story: null, next: null, preview: false } }

    const following = STORIES
        .slice(STORIES.indexOf(found) + 1)
        .find(s => s.kind === found.kind && (visibleTo(statusOf(s.id)) || reviewer))
    // Never throws: without a credit (or when the database does not answer) the quality line is simply not shown.
    const credit = await reviewCredit(storyReviewKey(found.id))

    return {
        props: {
            ...base,
            story: readerStory(found, status),
            next: following ? { id: following.id, title: { ...following.title } } : null,
            preview: !visible,
            credit
        }
    }
}

export default StoryPage
